import { useMemo } from 'react'
import type { FriendEventInterestBuckets } from './useFriendEventInterests'
import type { EventAttendanceStatus } from '../types'

export interface FacepileBucket {
  userIds: string[]
  goingUserIds: Set<string>
}

const EMPTY_MAP: Map<string, FacepileBucket> = new Map()
const EMPTY_SET: Set<string> = new Set()

export function useFacepileData(
  friendInterests: Map<string, FriendEventInterestBuckets>,
  eventStatuses: Map<string, EventAttendanceStatus>,
  currentUserId: string | null,
  eventScope?: Map<string, Set<string>>,
  explicitFriendIds: Set<string> = EMPTY_SET,
): Map<string, FacepileBucket> {
  return useMemo(() => {
    if (friendInterests.size === 0 && (!currentUserId || eventStatuses.size === 0)) {
      return EMPTY_MAP
    }
    const scopeFor = (eventId: string) => eventScope?.get(eventId)
    // Explicit friends always show; group-only co-members only show when the
    // group is linked to the event (i.e. they appear in eventScope).
    const filterFriends = (eventId: string, ids: string[]) => {
      if (!eventScope) return ids
      const allowed = scopeFor(eventId)
      return ids.filter(id => explicitFriendIds.has(id) || (allowed?.has(id) ?? false))
    }
    const map = new Map<string, FacepileBucket>()
    if (!currentUserId) {
      friendInterests.forEach((b, eventId) => {
        const going = filterFriends(eventId, b.going)
        const interested = filterFriends(eventId, b.interested)
        map.set(eventId, {
          userIds: [...going, ...interested],
          goingUserIds: new Set(going),
        })
      })
      return map
    }
    const eventIds = new Set<string>(friendInterests.keys())
    eventStatuses.forEach((_, eventId) => eventIds.add(eventId))
    eventIds.forEach(eventId => {
      const friends = friendInterests.get(eventId)
      const friendGoing = filterFriends(eventId, friends?.going ?? [])
      const friendInterested = filterFriends(eventId, friends?.interested ?? [])
      const selfStatus = eventStatuses.get(eventId)
      const going = selfStatus === 'going' ? [currentUserId, ...friendGoing] : friendGoing
      const interested =
        selfStatus === 'interested' ? [currentUserId, ...friendInterested] : friendInterested
      map.set(eventId, {
        userIds: [...going, ...interested],
        goingUserIds: new Set(going),
      })
    })
    return map
  }, [friendInterests, eventStatuses, currentUserId, eventScope, explicitFriendIds])
}
